import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, LogOut } from "lucide-react";
import { ColorChip } from "@/components/online/ColorPicker";
import { FieldError, OnlineShell, Panel } from "@/components/online/OnlineShell";
import { useLobby } from "@/hooks/useLobby";
import { clearOnline, errorText, getOnline, leaveLobby, saveOnline, startLobby } from "@/lib/api";

function MemberRow({ member, index, mine }) {
  return (
    <div data-testid={`lobby-member-${index}`} className="flex items-center justify-between gap-4 p-3 border border-[color:var(--line)] rounded-[8px]" style={{ opacity: member.gone ? 0.55 : 1 }}>
      <span className="flex items-center gap-3">
        <ColorChip color={member.color} />
        <span className="font-semibold text-[color:var(--ink)]">{member.name}{mine ? " (you)" : ""}</span>
      </span>
      <span className="flex items-center gap-2">
        {member.gone && <span className="badge-text text-[color:var(--ink-5)]" data-testid={`lobby-member-gone-${index}`}>Gone</span>}
        {member.is_leader && <span className="badge-text text-[color:var(--sea)]" data-testid={`lobby-member-leader-${index}`}>Leader</span>}
      </span>
    </div>
  );
}

async function copyLink(code) {
  const url = `${window.location.origin}/join/${code}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  } catch {
    window.prompt("Copy this link", url);
  }
}

function useLobbyRedirects(code, lobby, status) {
  const navigate = useNavigate();
  useEffect(() => {
    const ours = getOnline()?.code === code;
    if (status === "missing") {
      if (ours) clearOnline();
      toast.error("Lobby not found");
      navigate("/", { replace: true });
    } else if (lobby && (lobby.my_seat === null || lobby.my_seat === undefined)) {
      if (ours) clearOnline();
      navigate(`/join/${code}`, { replace: true });
    } else if (lobby?.status === "started" && lobby.game_id) {
      saveOnline({ ...getOnline(), game_id: lobby.game_id });
      navigate(`/game/${lobby.game_id}`, { replace: true });
    }
  }, [code, lobby, status, navigate]);
}

export default function LobbyScreen() {
  const code = useParams().code.toUpperCase();
  const navigate = useNavigate();
  const { lobby, status } = useLobby(code);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useLobbyRedirects(code, lobby, status);

  const me = lobby?.members?.[lobby.my_seat];
  const count = lobby?.members?.length || 0;

  const leave = async () => {
    setBusy(true);
    try {
      await leaveLobby(code);
    } catch {
      /* the ticket stops working either way */
    }
    clearOnline();
    navigate("/online");
  };

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { game_id } = await startLobby(code);
      saveOnline({ ...getOnline(), game_id });
      navigate(`/game/${game_id}`);
    } catch (err) {
      setError(errorText(err, "Could not start the game"));
      setBusy(false);
    }
  };

  return (
    <OnlineShell testId="lobby-screen" kicker="Waiting room" title="Lobby" back="/" backLabel="Home">
      <Panel testId="lobby-panel">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-kicker text-[color:var(--ink-3)]">Lobby code</div>
            <div className="font-coord text-4xl font-bold tracking-[0.25em] text-[color:var(--ink)] mt-1" data-testid="lobby-code">{code}</div>
          </div>
          <button type="button" className="btn-tactile" onClick={() => copyLink(code)} data-testid="lobby-copy-link-btn">
            <Copy size={16} strokeWidth={1.8} /> Copy link
          </button>
        </div>
        <div className="label-kicker text-[color:var(--ink-3)] mb-3" data-testid="lobby-member-count">{`Players ${count} of 4`}</div>
        <div className="space-y-2" data-testid="lobby-member-list">
          {(lobby?.members || []).map((m, i) => <MemberRow key={`${m.name}-${m.color}`} member={m} index={i} mine={i === lobby.my_seat} />)}
        </div>
        {status === "offline" && <p className="mt-4 text-sm text-[color:var(--ink-3)]" data-testid="lobby-reconnecting">Reconnecting…</p>}
        <FieldError testId="lobby-error">{error}</FieldError>
        <div className="mt-8 flex items-center justify-between gap-3">
          <button type="button" className="btn-ghost -ml-3" disabled={busy || !lobby} onClick={leave} data-testid="lobby-leave-btn">
            <LogOut size={16} strokeWidth={1.8} /> Leave
          </button>
          {me?.is_leader ? (
            <button type="button" className="btn-tactile btn-tactile--primary" disabled={busy || count < 2 || count > 4} onClick={start} data-testid="lobby-start-btn">
              {busy ? "Starting…" : "Start game →"}
            </button>
          ) : (
            <span className="text-sm text-[color:var(--ink-3)]" data-testid="lobby-waiting-for-leader">Waiting for the leader to start…</span>
          )}
        </div>
        {me?.is_leader && count < 2 && <p className="mt-3 text-xs text-right text-[color:var(--ink-3)]" data-testid="lobby-start-hint">Needs 2 to 4 players</p>}
      </Panel>
    </OnlineShell>
  );
}
